// TODO: temporary Vuex encapsulation. Once the Vuex store is retired,
// rewrite the internals to consume the new auth layer directly — the
// public API of `useAuth` is intended to stay stable so consumers
// (HeaderProfileMenu et al.) don't need to change.
import { computed, type ComputedRef } from 'vue';
import { useRouter } from 'vue-router';

import { clearImpFp, clearImpTokens, isImpersonating } from '@/auth/tabStorage';
import { useStore } from '@/composables/useVuexStore';
import * as auth from '@/utils/auth';
import { clearReloginContext } from '@/utils/reloginContext';

const ADMIN_ROLES: ReadonlyArray<string> = ['advert-admin', 'manager'];

export type AuthUser = {
  email: string;
};

export type UseAuthReturn = {
  user: ComputedRef<AuthUser | null>;
  role: ComputedRef<string>;
  availableRoles: ComputedRef<ReadonlyArray<string>>;
  isAdmin: ComputedRef<boolean>;
  isImpersonatingTab: ComputedRef<boolean>;
  switchRole: (nextRole: string) => void;
  switchUser: (email: string) => void;
  exitImpersonation: () => void;
  logout: () => void;
};

export const useAuth = (): UseAuthReturn => {
  const store = useStore();
  const router = useRouter();

  const user = computed<AuthUser | null>(() => store.getters.user ?? null);
  const role = computed<string>(() => store.getters.role);
  const availableRoles = computed<ReadonlyArray<string>>(() => store.getters.availableRoles ?? []);
  const isAdmin = computed<boolean>(() => availableRoles.value.some((r) => ADMIN_ROLES.includes(r)));
  const isImpersonatingTab = computed<boolean>(() => isImpersonating());

  const switchRole = (nextRole: string): void => {
    clearReloginContext();
    Promise.resolve(store.dispatch('switchRole', nextRole)).then(
      () => {
        window.location.reload();
      },
      () => {
        window.location.reload();
      },
    );
  };

  const switchUser = (email: string): void => {
    auth.saveUser({ email });
    store.dispatch('fetchUser').then((fetched: unknown) => {
      if (fetched !== null && fetched !== undefined) {
        router.go(0);
      }
    });
  };

  // Exit impersonation: clear the per-tab sessionStorage token so this tab
  // falls back to reading the admin's real localStorage tokens on next reload.
  const exitImpersonation = (): void => {
    const fakeLogin = auth.readFakeLogin();
    if (fakeLogin.admin) {
      auth.saveUser({ email: fakeLogin.admin });
    }
    if (fakeLogin.adminRole) {
      auth.saveActiveRole(fakeLogin.adminRole);
      sessionStorage.setItem('role', fakeLogin.adminRole);
    }
    clearImpTokens();
    clearImpFp();
    auth.saveFakeLogin();
    window.location.reload();
  };

  const logout = (): void => {
    store.dispatch('logout');
  };

  return { user, role, availableRoles, isAdmin, isImpersonatingTab, switchRole, switchUser, exitImpersonation, logout };
};
